import { Button, Checkbox, DatePicker, Form, Select, Space } from "antd";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { BCFStore } from "Components/XKTViewer/Stores/BCFStore";
import { I18N_NS } from "Config/i18n";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsDataStore } from "Stores";

interface BcfFilterTopicsFormProps extends ModalComponentProps {
  bcfStore: BCFStore;
}

export const BcfFilterTopicsForm = observer(({ bcfStore, hideModal }: BcfFilterTopicsFormProps) => {
  const { t } = useTranslation([I18N_NS.PROJECT, I18N_NS.XKT_VIEWER]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAllIssues, setShowAllIssues] = useState(false);

  useEffect(() => {
    setShowDeleted(bcfStore.showDeletedTopics);
    setShowAllIssues(bcfStore.showAllIssues);
  }, [bcfStore.showDeletedTopics, bcfStore.showAllIssues]);

  const handleFinish = (values) => {
    // Always update display options and fetch fresh data on submit
    bcfStore.updateDisplayOptions({
      showDeleted: showDeleted,
      showAllIssues: showAllIssues,
    });
    bcfStore.filterTopics(values);
    hideModal();
  };

  const handleResetFilter = () => {
    bcfStore.filterTopics({});
    bcfStore.updateDisplayOptions({
      showDeleted: false,
      showAllIssues: false,
    });
    hideModal();
  };

  return (
    <Form
      onFinish={handleFinish}
      id="bcf_topic_filter"
      initialValues={toJS(bcfStore.topicFilterParams)}
      layout="vertical"
    >
      {projectsDataStore.isAdmin && (
        <Form.Item style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Checkbox
            title={t("Include marked as deleted")}
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>{t("Include marked as deleted")}</span>
        </Form.Item>
      )}
      <Form.Item style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Checkbox
          title={t("Also include issues that are not linked to the currently open models")}
          checked={showAllIssues}
          onChange={(e) => setShowAllIssues(e.target.checked)}
        />
        <span style={{ marginLeft: 8 }}>{t("Show all issues")}</span>
      </Form.Item>
      <Form.Item name="topic_type" label={t("Type")}>
        <Select<string>
          placeholder={t("Type")}
          options={bcfStore.extensions.topic_type.map((item) => ({ label: item, value: item }))}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="topic_status" label={t("Status")}>
        <Select<string>
          placeholder={t("Status")}
          options={bcfStore.extensions.topic_status.map((item) => ({ label: item, value: item }))}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="priority" label={t("Priority")}>
        <Select<string>
          placeholder={t("Priority")}
          options={bcfStore.extensions.priority.map((item) => ({ label: item, value: item }))}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="creation_author_id" label={t("Author")}>
        <Select
          placeholder={t("Author")}
          showSearch={{ filterOption: true }}
          options={bcfStore.projectUsers.slice()}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="creation_date" label={t("Created on")}>
        <DatePicker
          placeholder={t("Created on")}
          format="L"
          style={{ width: "100%" }}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="assigned_to_id" label={t("Assigned to")}>
        <Select
          placeholder={t("Assigned to")}
          options={bcfStore.assignedToOptions.slice()}
          showSearch={{ optionFilterProp: "label", filterOption: true }}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="stage" label={t("Stage")}>
        <Select
          placeholder={t("Stage")}
          showSearch={{ filterOption: true }}
          options={bcfStore.stageOptions}
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <Form.Item name="labels" label={t("Label")}>
        <Select
          placeholder={t("Labels")}
          showSearch={{ filterOption: true }}
          options={bcfStore.labelOptions}
          mode="multiple"
          getPopupContainer={() => document.getElementById("bcf_topic_filter")}
          allowClear
        />
      </Form.Item>
      <ButtonsRow justify="space-between">
        <Button onClick={hideModal}>{t("Cancel")}</Button>
        <Space size="small">
          <Button onClick={handleResetFilter}>{t("Reset")}</Button>
          <Button type="primary" htmlType="submit">
            {t("Submit")}
          </Button>
        </Space>
      </ButtonsRow>
    </Form>
  );
});
